import { Vec3 } from '../vector';
import type { CameraIntrinsics } from './types';

export function calculateColmapFov(camera: CameraIntrinsics): number {
  const focal = camera.params[0];
  if (!focal || focal <= 0) return 90;

  switch (camera.model.toUpperCase()) {
    case 'SIMPLE_PINHOLE':
    case 'PINHOLE':
    case 'SIMPLE_RADIAL':
    case 'RADIAL':
    case 'OPENCV':
    case 'OPENCV_FISHEYE':
    case 'FULL_OPENCV':
      return (2 * Math.atan(camera.width / (2 * focal)) * 180) / Math.PI;
    default:
      return 90;
  }
}

/**
 * COLMAP 外参 (qvec, tvec) → 展示空间相机世界坐标。
 *
 * 内部先做 C = -R^T * t 得到 COLMAP 原生坐标，
 * 再映射 (-x, z, y) 对齐 gsplat entity 的最终朝向
 * （entity 叠加了 loader Rz(180°) + baseRotation Rx(+90°)）。
 */
export function cameraPositionFromQvecTvec(qvec: number[], tvec: number[]): Vec3 {
  const r = rotationMatrixFromQvec(qvec);
  const [tx, ty, tz] = tvec;
  // C = -R^T * t，然后 (-Cx, Cz, Cy)
  return new Vec3(
    r[0][0] * tx + r[1][0] * ty + r[2][0] * tz,
    -(r[0][2] * tx + r[1][2] * ty + r[2][2] * tz),
    -(r[0][1] * tx + r[1][1] * ty + r[2][1] * tz),
  );
}

/**
 * COLMAP qvec → 展示空间相机朝向（单位向量）。
 * R 第三行为 COLMAP 原生 forward，同样映射 (-x, z, y)。
 */
export function cameraForwardFromQvec(qvec: number[]): Vec3 {
  const r = rotationMatrixFromQvec(qvec);
  return new Vec3(-r[2][0], r[2][2], r[2][1]).normalize(Vec3.FORWARD);
}

function normalizeQuaternion(qvec: number[]): [number, number, number, number] {
  const qw = qvec[0];
  const qx = qvec[1];
  const qy = qvec[2];
  const qz = qvec[3];
  const len = Math.hypot(qw, qx, qy, qz) || 1;
  return [qw / len, qx / len, qy / len, qz / len];
}

function rotationMatrixFromQvec(qvec: number[]): number[][] {
  const [qw, qx, qy, qz] = normalizeQuaternion(qvec);
  return [
    [1 - 2 * qy ** 2 - 2 * qz ** 2, 2 * qx * qy - 2 * qz * qw, 2 * qx * qz + 2 * qy * qw],
    [2 * qx * qy + 2 * qz * qw, 1 - 2 * qx ** 2 - 2 * qz ** 2, 2 * qy * qz - 2 * qx * qw],
    [2 * qx * qz - 2 * qy * qw, 2 * qy * qz + 2 * qx * qw, 1 - 2 * qx ** 2 - 2 * qy ** 2],
  ];
}
